<template>
  <v-container class="py-8 bg-light-blue-main">
    <v-btn
      prepend-icon="mdi-arrow-left"
      @click="router.back()"
      >Zurück</v-btn
    >
    <h1>{{ $t("views.organization.edit") }}</h1>

    <v-card>
      <v-card-text>
        <v-form>
          <v-text-field
            data-cy="organization-name"
            class="mb-4 mt-2"
            v-model="name"
            :rules="[required]"
            :disabled="!allowedEdit"
            label="Name" />
          <v-text-field
            data-cy="organization-description"
            v-model="description"
            :disabled="!allowedEdit"
            label="Description" />
        </v-form>
      </v-card-text>
    </v-card>
    <template v-if="current_status !== 'paid'">
      <v-alert
        color="error"
        :dismissible="false">
        <span>{{ $t("views.licence.other.not-paid") }}</span>
        <v-spacer></v-spacer>
        <v-btn
          style="color: black"
          v-if="current_status !== 'draft'"
          @click="payNow">
          {{ $t("views.licence.other.pay-now") }}
        </v-btn>
      </v-alert>
    </template>
    <div :class="current_status !== 'paid' ? 'disabled-change-module' : ''">
      <v-card>
        <v-card-text>
          <v-data-table
            :headers="userListHeaders"
            :items="userListItems">
            <template v-slot:header.actions="{}">
              <v-btn
                v-if="allowedEdit"
                data-cy="add-user"
                @click="addUser()"
                ><v-icon>mdi-plus</v-icon></v-btn
              >
            </template>
            <template
              v-if="allowedEdit"
              v-slot:item.actions="{ item, index }">
              <v-btn
                v-if="item.state !== 2"
                :data-cy="`edit-user-${item.email.replaceAll(/[^\w]/g, '')}`"
                @click="editUser(item, index)"
                ><v-icon>mdi-pencil</v-icon></v-btn
              >
              <v-btn
                :data-cy="`remove-user-${item.email}`"
                v-if="item.rights !== 1"
                @click="removeUser(index)"
                ><v-icon>mdi-delete</v-icon></v-btn
              >
            </template>
          </v-data-table>
          <v-dialog
            v-model="userDialog.dialog"
            persistent>
            <v-card
              width="60%"
              class="mx-auto">
              <v-card-title>
                {{ $t("views.organization.editUser") }}
              </v-card-title>
              <v-card-text>
                <v-form ref="userForm">
                  <v-text-field
                    class="mt-2"
                    data-cy="dialog-user-email"
                    :disabled="userDialog.rights === 1"
                    v-model="userDialog.email"
                    :rules="emailRules"
                    label="Email" />
                  <v-select
                    data-cy="dialog-user-rights"
                    class="mt-4"
                    :disabled="userDialog.rights === 1"
                    v-model="userDialog.rights"
                    :items="
                      userRights.filter(
                        (option) =>
                          userDialog.rights === 1 || option.value !== 1
                      )
                    ">
                    <template v-slot:item="{ props: itemProps, item }">
                      <v-list-item v-bind="itemProps">
                        <template v-slot:title>
                          {{ item.title }}
                          {{ itemProps.extra }}
                        </template>
                      </v-list-item>
                    </template>
                  </v-select>
                  <RightsElement
                    data-cy="dialog-user-modules"
                    label="Lizenz"
                    class="mt-4"
                    v-model="userDialog.modules"
                    :options="moduleSelectOptions"></RightsElement>
                </v-form>
              </v-card-text>
              <v-card-actions>
                <v-btn
                  data-cy="dialog-user-save"
                  @click="userDialog.callback()"
                  >{{ $t("views.organization.save") }}</v-btn
                >
                <v-btn
                  data-cy="dialog-user-cancel"
                  @click="userDialog.dialog = false"
                  >{{ $t("views.organization.cancel") }}</v-btn
                >
              </v-card-actions>
            </v-card>
          </v-dialog>
        </v-card-text>
      </v-card>

      <v-card>
        <ModuleListSlots
          v-if="loaded"
          ref="moduleList"
          :allowedEdit="false"
          :slots="allModules"
          :allSlots="modulesBefore"
          :current_status="current_status"
          :licence_id="licenceId"></ModuleListSlots>
      </v-card>
      <v-card v-if="allowedEdit">
        <v-card-actions>
          <DeleteWithConfirmationButton
            :deleteAction="
              () => mainStore.organizationDelete(Number(organizationId))
            " />

          <v-spacer></v-spacer>
          <v-btn @click="router.back()">Abbrechen</v-btn>
          <v-btn
            color="success"
            data-cy="save-organization"
            @click="save"
            >Speichern</v-btn
          >
        </v-card-actions>
      </v-card>
    </div>
  </v-container>
</template>

<script setup lang="ts">
import { computed, onMounted, Ref, ref, useTemplateRef } from "vue";
import { useUserRules } from "@/validations/user";
import { useCommonRules } from "@/validations/common";
import { useTranslation } from "i18next-vue";
import { useMainStore } from "@/stores/mainStore";
import { useAuthStore } from "@/stores/authStore";
import axios from "axios";
import {
  ORGANIZATION_RIGHT_COOWNER,
  ORGANIZATION_RIGHT_OWNER,
  ORGANIZATION_RIGHT_READ,
  organizationRightNames
} from "@/utils/constants";
import RightsElement from "@/components/Elements/RightsElement.vue";
import { useAxiosStore } from "@/stores/axiosStore";
import { useRoute, useRouter } from "vue-router";
import { useConfirm, useSnackbar } from "vuetify-use-dialog";
import ModuleListSlots from "../Licence/ModuleListSlots.vue";
import DeleteWithConfirmationButton from "@/components/tools/DeleteWithConfirmationButton.vue";

const createConfirm = useConfirm();
const createSnackbar = useSnackbar();
const route = useRoute();
const router = useRouter();

const allowedEdit = computed(() => {
  return (
    mainStore.organizationRights(route.params.id as string) &
    (ORGANIZATION_RIGHT_OWNER | ORGANIZATION_RIGHT_COOWNER)
  );
});

type User = {
  email: string;
  rights: number;
  modules: number;
  newModules: number;
  state: number;
  pending?: number;
};

const organizationId = computed(() => route.params.id);

const mainStore = useMainStore();
const authStore = useAuthStore();
const axiosStore = useAxiosStore();

const { t } = useTranslation();

const { emailRules } = useUserRules(t);
const { required } = useCommonRules(t);

const licenceId = ref(0);
const current_status = ref("paid");

const moduleListRef = useTemplateRef("moduleList");

onMounted(() => {
  loadOrganization();
});

const loaded = ref(false);

const moduleSelectOptions = computed(() => {
  return Object.values(mainStore.modules)
    .map((module) => ({
      title: t("views.licence.modules." + module.name + ".name"),
      value: module.value,
      props: {
        "data-cy": `select-option-${module.name}`,
        requires: module.requires,
        valueName: module.name
      }
    }))
    .filter((option) => Number(option.value) !== 32);
});

function formatModules(
  modules: Record<string, number>
): Record<number, number> {
  console.log("Hier", modules);
  return Object.fromEntries(
    mainStore.modules.map((module) => [module.value, modules[module.name]])
  );
}

async function loadOrganization() {
  const {
    data: { organization, licence }
  } = await axios.get("/api/organization/" + organizationId.value);

  name.value = organization.name;
  description.value = organization.description;

  modules.value = licence.modules;
  modulesBefore.value = formatModules(licence.allModules);

  console.log(licence, organization);

  for (const user of organization.organization_user) {
    userListItems.value.push({
      email: user.users.email,
      rights: user.rights,
      modules: Number(user.licence_modules),
      newModules: Number(user.licence_modules_new),
      state: user.state
    });
  }

  for (const user of organization.pending_invites) {
    userListItems.value.push({
      email: user.email,
      rights: user.rights,
      modules: Number(user.licence_modules),
      newModules: Number(user.licence_modules),
      state: 0,
      pending: user.id
    });
  }

  licenceId.value = licence.id;
  current_status.value = licence.currentStatus;

  loaded.value = true;
}

const allModules = computed(() => {
  return userListItems.value.reduce(
    (acc, user) => {
      for (const module of mainStore.modules) {
        acc[module.value] = acc[module.value] || 0;
        if (current_status.value !== "paid") {
          acc[module.value] += user.newModules & module.value ? 1 : 0;
        } else {
          acc[module.value] += user.modules & module.value ? 1 : 0;
        }
      }
      return acc;
    },
    { 32: 1 }
  );
});

const name = ref("");
const description = ref("");
const mode = ref(0);
const modules = ref({});
const modulesBefore = ref({});
const userListItems: Ref<User[]> = ref([]);

const userRights = Object.entries(organizationRightNames).map(
  ([key, value]) => ({
    title: t("other.rights." + value),
    value: Number(key),
    props: {
      "data-cy": `select-option-${value}`,
      extra: t("other.rights." + value + "-extra")
    }
  })
);

const userListHeaders = [
  { title: t("views.organization.userList.email"), value: "email" },
  {
    title: t("views.organization.userList.rights"),
    key: "rights",
    value: (item) =>
      userRights
        .filter((option) => option.value & item.rights)
        .map((option) => option.title)
        .join(", ")
  },
  {
    title: t("views.organization.userList.licence"),
    key: "modules",
    value: (item) =>
      moduleSelectOptions.value
        .filter((option) =>
          current_status.value !== "paid"
            ? option.value & item.newModules
            : option.value & item.modules
        )
        .map((option) => option.title)
        .join(", ") || "Keine",
    width: 300
  },
  {
    title: "",
    key: "state",
    value: (item) => {
      console.log("state", item.state);
      return stateOptions[item.state];
    }
  },
  { title: "actions", value: "actions" }
];

const stateOptions = {
  0: t("other.state.inactive"),
  1: "", //t("other.state.active"),
  2: t("other.state.declined")
};

const userFormRef = useTemplateRef("userForm");

const userDialog = ref({
  dialog: false,
  email: "",
  rights: ORGANIZATION_RIGHT_READ,
  modules: 0,
  index: -1,
  callback: () => {}
});

const userListEmails = computed(() => {
  return userListItems.value.map((item) => item.email);
});

function addUser() {
  userDialog.value = {
    dialog: true,
    email: "",
    rights: ORGANIZATION_RIGHT_READ,
    modules: 0,
    index: -1,
    callback: () => {
      userFormRef.value.validate().then((valid) => {
        if (valid.valid) {
          if (
            userDialog.value.email &&
            !userListEmails.value.includes(userDialog.value.email)
          ) {
            userListItems.value.push({
              email: userDialog.value.email,
              rights: userDialog.value.rights,
              modules: userDialog.value.modules,
              newModules: userDialog.value.modules,
              state: 0
            });
            userDialog.value.dialog = false;
          }
        }
      });
    }
  };
}

function editUser(item: User, index: number) {
  userDialog.value = {
    dialog: true,
    email: item.email,
    rights: item.rights,
    modules: item.modules,
    index: index,
    callback: () => {
      const user = userListItems.value.splice(index, 1)[0];
      userListItems.value.splice(index, 0, {
        email: userDialog.value.email,
        rights: userDialog.value.rights,
        modules: userDialog.value.modules,
        newModules: userDialog.value.modules,
        state: user.state,
        pending: user.pending
      });
      userDialog.value.dialog = false;
    }
  };
}

function removeUser(index: number) {
  userListItems.value.splice(index, 1);
}

function saveChanges() {
  console.log("saveChanges");

  if (!moduleListRef.value) {
    axiosStore.execute({
      method: "POST",
      url: "/api/organization/edit/" + organizationId.value,
      data: {
        name: name.value,
        description: description.value,
        modules: modules.value,
        users: userListItems.value,
        licenceId: licenceId.value
      }
    });
    return;
  }

  const { monthlyCost, additionalCharge, modulesRemoved } =
    moduleListRef.value.getInfos();

  const sendModules = computed(() => {
    return Object.fromEntries(
      Object.entries(allModules.value).map(([key, value]) => [
        mainStore.modules.find((module) => Number(module.value) === Number(key))
          .name,
        value
      ])
    );
  });

  // payment for remaining days of the month
  //
  createConfirm({
    dialogProps: { width: 400 },
    cardProps: { color: "warning" },
    title: t(`actions.confirmation.change-licence.title`),
    content:
      t(`actions.confirmation.change-licence.content`, {
        newPrice: monthlyCost
      }) +
      (additionalCharge
        ? t(
            `actions.confirmation.change-licence.module-added`,
            additionalCharge
          )
        : "") +
      (modulesRemoved.length
        ? t(`actions.confirmation.change-licence.module-removed`, {
            removedModules: modulesRemoved
              .map((module) => module.module)
              .join(", ")
          })
        : ""),
    cardTextProps: { style: { "white-space": "pre-wrap" } },
    confirmationText: t(`actions.confirmation.change-licence.confirm`),
    cancellationText: t(`actions.confirmation.change-licence.cancel`)
    // confirmationButtonProps: { color: "warning" }
  }).then((confirm) => {
    if (confirm) {
      axiosStore.execute(
        {
          method: "POST",
          url: "/api/organization/edit/" + organizationId.value,
          data: {
            name: name.value,
            description: description.value,
            modules: sendModules.value,
            users: userListItems.value,
            licenceId: licenceId.value
          }
        },
        () => {
          router.push({ name: "profile" });
        }
      );

      createSnackbar({
        text: t("actions.saved"),
        showCloseButton: false,
        snackbarProps: { color: "error", timeout: 1000 }
      });
    }
  });
}

function save() {
  if (!moduleListRef.value) {
    console.log("Error");
    return;
  }
  const sendModules = computed(() => {
    return Object.fromEntries(
      Object.entries(allModules.value).map(([key, value]) => [
        mainStore.modules.find((module) => Number(module.value) === Number(key))
          .name,
        value
      ])
    );
  });

  moduleListRef.value.saveModules(() => {
    axiosStore.execute(
      {
        method: "POST",
        url: "/api/organization/edit/" + organizationId.value,
        data: {
          name: name.value,
          description: description.value,
          modules: sendModules.value,
          users: userListItems.value,
          licenceId: licenceId.value
        }
      },
      (response) => {
        open(response.data, "_self");

        // mainStore.loadOrganizations();

        // router.push({ name: "profile" });
      }
    );
  });
}

function payNow() {
  if (!current_status.value) {
    save();
  } else {
    axios
      .get("/api/stripe/latest-invoice/" + licenceId.value)
      .then((response) => {
        console.log("Success", response.data);
        window.open(response.data.hosted_invoice_url, "_blank");
      });
  }
}
</script>
<style>
.disabled-change-module {
  opacity: 0.7;
  pointer-events: none;
}
</style>
